import React, { useState, useEffect, FunctionComponent } from "react"
import { IconContext } from "react-icons/lib"
import { RiCheckFill } from "react-icons/ri"
import { useNavigate } from "react-router-dom"
import { convertLevels, convertTrainingId, convertCoursePriority } from "@murphy-frontend/common/utils"
import dayjs from "dayjs"
import { useDimensions } from "../../../hooks/useDimensions"
import logo from "../../../../assets/img/Praeparatus_Supervivet_Practise_Risk.jpg"
import FilterTag from "@murphy-frontend/web-core/components/FilterTag"
import { useUserPreferences } from "../../../contexts/UserPreferencesContext"
import { PiLockKeyFill } from "react-icons/pi"
import { useCustomer } from "../../../contexts/CustomerContext"

interface CourseCardProps {
	title: string
	description: string
	footer: any
	bookedTrainingSession: any
	completedTrainingSessions: any
	backGroundUrl: any
	trainingId: any
	translations: Record<string, string>
	selectedTrainingSession: any
	trainingTypeId: number
	level?: number,
	freemium: boolean,
	isMandatory: boolean
	onClick?: () => {}
}

const CourseCard: FunctionComponent<CourseCardProps> = ({
	title,
	description,
	footer,
	bookedTrainingSession,
	completedTrainingSessions,
	backGroundUrl,
	trainingId,
	translations,
	selectedTrainingSession,
	trainingTypeId,
	level,
	freemium,
	isMandatory,
	onClick
}) => {
	const navigate = useNavigate()
	const [snipDescription, setSnipDescription] = useState(false)
	const [coursecardTextClass, setCoursecardTextClass] = useState("course-card-text")
	const [isExpanded, setIsExpanded] = useState(false)
	const [ref, { height }] = useDimensions()
	const { language } = useUserPreferences()
	const { customer } = useCustomer();

	// Map language codes to HTML lang attribute values
	const langMap = {
		'swe': 'sv',
		'eng': 'en',
		'nor': 'nb',
		'fin': 'fi',
		'dan': 'da'
	}

	const onClickImage = () => {
		const url = `/coursedescription/${trainingId}`
		navigate(url)
	}

	useEffect(() => {
		if (isExpanded) {
			setCoursecardTextClass("course-card-text-expanded")
		} else if (height) {
			if (height > 140) {
				setSnipDescription(true)
				setCoursecardTextClass("course-card-text course-card-text-more")
			} else {
				setSnipDescription(false)
				setCoursecardTextClass("course-card-text")
			}
		} else {
			setSnipDescription(false)
			setCoursecardTextClass("course-card-text")
		}
	}, [height, isExpanded])

	const onClickExpand = () => {
		setIsExpanded(!isExpanded)
	}

	const formatString = (str: string) => {
		const lines = str.split(/\n/)
		return lines.map((line, index) => (
			<React.Fragment key={index}>
				{line}
				<br />
			</React.Fragment>
		))
	}

	return (
		<div className='course-card-container' lang={langMap[language] || 'en'}>
			<div className='course-card-header'>
				<div
					className='course-card-background-image'
					style={{
						backgroundImage: `url(${backGroundUrl ?? logo})`,
						backgroundSize: "cover",
						backgroundRepeat: "no-repeat",
						backgroundPosition: "center"
					}}
					onClick={onClickImage}
				>
					{bookedTrainingSession && (
						<div className='course-card-state-icon-container'>
							<IconContext.Provider value={{ className: "course-card-state-icon" }}>
								<RiCheckFill />
							</IconContext.Provider>
						</div>
					)}
					{bookedTrainingSession && (
						<>
							<div className='course-card-state-date'>
								<b>{dayjs(bookedTrainingSession?.trainingSessionStartDate).format("ddd DD MMM")}</b>{" "}
								{dayjs(bookedTrainingSession?.trainingSessionStartDate).format("HH:mm")}
								{"-"}
								{dayjs(bookedTrainingSession?.trainingSessionEndDate).format("HH:mm")}
							</div>
						</>
					)}
					<div className='course-card-completed-sessions'>
						{(completedTrainingSessions && completedTrainingSessions.length > 0) ? (
							<div className='course-card-completed-icon'>
								{completedTrainingSessions[0]?.trainingSessionStateName}
							</div>
						) : (isMandatory && (
							<div className='course-card-mandatory-icon'>
								{translations.mandatory}
							</div>
						))}
						{bookedTrainingSession && (
							<div className='course-card-booked-icon'>
								{bookedTrainingSession?.trainingSessionStateName}
							</div>
						)}
					</div>
				</div>
			</div>
			<div className='course-card-body'>
				<div className='course-card-description'>
					<h2>{title}</h2>
					<div className={coursecardTextClass}>
						{Number.isInteger(selectedTrainingSession[0]?.AttendeeSpotLeft) && (
							<div className='course-spots-left-container'>
								<h6>{translations.spotsleft}</h6>
								<h6
									className={
										selectedTrainingSession[0]?.AttendeeSpotLeft < 4
											? `course-spots-left_few`
											: `course-spots-left`
									}
								>
									{` ${selectedTrainingSession[0]?.AttendeeSpotLeft}`}
								</h6>
							</div>
						)}
						<p ref={ref}>{formatString(description)}</p>
					</div>
					{snipDescription === true && (
						<div className='course-card-more'>
							{!isExpanded && <p onClick={onClickExpand}>{translations.showmore} &gt;</p>}
						</div>
					)}
				</div>
				<div className='course-card-tags'>
					<div className='flex-row-container row-with-gaps'>
						<FilterTag filterName={convertTrainingId(trainingTypeId, translations)} passive={true} />
						{level && <FilterTag filterName={convertLevels(level, translations)} passive={true} />}
						{/* <FilterTag filterName={"2 timmar"} inActive={true} /> */}
					</div>
				</div>
			</div>
			<div className='course-card-footer'>{footer}</div>
			{!freemium && customer.IsFreemium && <div onClick = {onClick} style={{position: 'absolute', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1, justifyContent: 'center', alignItems: 'center', cursor: 'pointer'}} className='course-card-container'>
				<PiLockKeyFill size={40}/>
			</div>}
		</div>
	)
}

export default CourseCard
